"use client";

import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { DatamuseCredit } from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { fetchDatamuse, mapLimited } from "@/lib/tool-kit/vocabulary/datamuse";
import {
  dictionaryPronunciation,
  guessedPronunciation,
  hasDigits,
  ipaFor,
  type Pronunciation,
  pronunciationOf,
  pronunciationQuery,
  type TextToken,
  tokenizeText,
  transcribe,
  uniqueWords,
  wrapLines,
} from "@/lib/tool-kit/vocabulary/ipa";
import {
  loadPronouncingDictionary,
  type PronouncingDictionary,
} from "@/lib/tool-kit/vocabulary/pronouncing-dictionary";

/** English strings. A translated content file overrides these through `ui`. */
export const ENGLISH_TO_IPA_CONVERTER_STRINGS = {
  textLabel: "English text",
  textPlaceholder: "The quick brown fox jumps over the lazy dog.",
  textHint:
    "Up to {max} different words at a time. Numbers are not transcribed — write them as words.",
  convert: "Convert to IPA",
  clear: "Clear",
  trySample: "Try a sample",
  sample: "Research shows that memory improves after a good night's sleep.",
  stressLabel: "Show stress marks (ˈ primary, ˌ secondary)",
  slashesLabel: "Wrap each line in slashes",
  emptyPrompt: "Type or paste English text, then choose Convert to IPA.",
  progress: "Looking up pronunciations: {done} of {total}…",
  truncated:
    "Only the first {max} different words are transcribed; the rest are left as written.",
  digits: "Numbers are left as written — spell them out to transcribe them.",
  heading: "IPA transcription",
  accentNote:
    "General American pronunciations from the CMU Pronouncing Dictionary — how each word is usually said in the dictionary's form, not a transcription of your own accent.",
  copyTranscription: "Copy transcription",
  copied: "Copied!",
  wordsHeading: "Word by word",
  colWord: "Word",
  colIpa: "IPA",
  colArpabet: "CMU (ARPAbet)",
  colNote: "Note",
  guessed: "Not in the dictionary — guessed from the spelling",
  failed: "Lookup failed — left as written",
  guessedCount:
    "one:{count} word is not in the dictionary, so its pronunciation is a guess from the spelling.|other:{count} words are not in the dictionary, so their pronunciations are guesses from the spelling.",
  failedCount:
    "one:{count} word could not be looked up and is left as written.|other:{count} words could not be looked up and are left as written.",
  retryFailed: "Try those words again",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof ENGLISH_TO_IPA_CONVERTER_STRINGS;

const MAX_WORDS = 300;
const CONCURRENCY = 4;

interface Run {
  tokens: TextToken[];
  words: string[];
  truncated: boolean;
  digits: boolean;
  found: Record<string, Pronunciation>;
  failed: string[];
  done: number;
  total: number;
  running: boolean;
}

/** Fill `found` with the words the dictionary has; return the others, in order. */
function fromDictionary(
  dictionary: PronouncingDictionary | null,
  words: readonly string[],
  found: Record<string, Pronunciation>
): string[] {
  const missing: string[] = [];
  for (const word of words) {
    const arpabet = dictionary?.lookup(word) ?? null;
    if (arpabet) {
      found[word] = dictionaryPronunciation(word, arpabet);
    } else {
      missing.push(word);
    }
  }
  return missing;
}

/**
 * The lookups for one conversion; a new conversion or leaving the page cancels them.
 * The bundled CMU dictionary answers most words in one download; only the words it
 * lacks are asked of Datamuse, one request each. If the dictionary cannot be loaded,
 * every word is asked of Datamuse instead.
 */
function usePronunciations() {
  const [run, setRun] = useState<Run | null>(null);
  const current = useRef<AbortController | null>(null);

  useEffect(() => () => current.current?.abort(), []);

  async function lookUp(base: Run, words: readonly string[]) {
    current.current?.abort();
    const controller = new AbortController();
    current.current = controller;
    const found = { ...base.found };
    const failed: string[] = [];
    setRun({
      ...base,
      failed: [],
      done: 0,
      total: words.length,
      running: true,
    });
    const dictionary = await loadPronouncingDictionary().catch(() => null);
    if (controller.signal.aborted) {
      return;
    }
    const missing = fromDictionary(dictionary, words, found);
    const answered = words.length - missing.length;
    setRun((previous) =>
      previous ? { ...previous, done: answered, found: { ...found } } : previous
    );
    const outcome = await mapLimited(
      missing,
      async (word) => {
        try {
          const results = await fetchDatamuse(
            pronunciationQuery(word),
            controller.signal
          );
          found[word] = dictionary
            ? guessedPronunciation(word, results)
            : pronunciationOf(word, results);
        } catch (error) {
          if (controller.signal.aborted) {
            throw error;
          }
          failed.push(word);
        }
      },
      {
        concurrency: CONCURRENCY,
        signal: controller.signal,
        onProgress: (done) =>
          setRun((previous) =>
            previous
              ? { ...previous, done: answered + done, found: { ...found } }
              : previous
          ),
      }
    );
    if (outcome !== null) {
      setRun((previous) =>
        previous
          ? { ...previous, found: { ...found }, failed, running: false }
          : previous
      );
    }
  }

  function convert(text: string) {
    const tokens = tokenizeText(text);
    const all = uniqueWords(tokens);
    const words = all.slice(0, MAX_WORDS);
    lookUp(
      {
        tokens,
        words,
        truncated: all.length > MAX_WORDS,
        digits: hasDigits(text),
        found: {},
        failed: [],
        done: 0,
        total: words.length,
        running: true,
      },
      words
    );
  }

  function retry() {
    if (run) {
      lookUp(run, run.failed);
    }
  }

  function reset() {
    current.current?.abort();
    setRun(null);
  }

  return { run, convert, retry, reset };
}

function WordTable({
  run,
  stressMarks,
  strings,
}: {
  run: Run;
  stressMarks: boolean;
  strings: Strings;
}) {
  const failed = new Set(run.failed);
  return (
    <div className="max-h-[480px] overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.colWord}
            </th>
            <th className={TH} scope="col">
              {strings.colIpa}
            </th>
            <th className={TH} scope="col">
              {strings.colArpabet}
            </th>
            <th className={TH} scope="col">
              {strings.colNote}
            </th>
          </tr>
        </thead>
        <tbody>
          {run.words.map((word) => {
            const found = run.found[word];
            let note = "";
            if (failed.has(word)) {
              note = strings.failed;
            } else if (found && !found.known) {
              note = strings.guessed;
            }
            return (
              <tr key={word}>
                <td className={TD}>{word}</td>
                <td className={`${TD} text-base`}>
                  {found ? ipaFor(found, { stressMarks }) : ""}
                </td>
                <td className={`${TD} font-mono text-muted-foreground text-xs`}>
                  {found?.arpabet ?? ""}
                </td>
                <td className={`${TD} text-muted-foreground text-xs`}>
                  {note}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Results({
  run,
  stressMarks,
  slashes,
  onRetry,
  strings,
}: {
  run: Run;
  stressMarks: boolean;
  slashes: boolean;
  onRetry: () => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const plain = transcribe(run.tokens, (key) => {
    const found = run.found[key];
    return found ? ipaFor(found, { stressMarks }) : null;
  });
  const output = slashes ? wrapLines(plain) : plain;
  const guessed = run.words.filter(
    (word) => run.found[word] && !run.found[word].known
  ).length;
  return (
    <section className="space-y-5">
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        {run.running ? (
          <p className={HINT}>
            {t(strings.progress, {
              done: fmt(run.done),
              total: fmt(run.total),
            })}
          </p>
        ) : null}
      </div>
      <div className={RESULT_CARD}>
        <p className="whitespace-pre-wrap text-foreground text-lg leading-relaxed">
          {output}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          disabled={run.running}
          label={strings.copyTranscription}
          text={output}
        />
      </div>
      <p className={HINT}>{strings.accentNote}</p>
      {run.truncated ? (
        <Notice>{t(strings.truncated, { max: fmt(MAX_WORDS) })}</Notice>
      ) : null}
      {run.digits ? <Notice>{strings.digits}</Notice> : null}
      {!run.running && guessed > 0 ? (
        <Notice>
          {tPlural(strings.guessedCount, locale, guessed, {
            count: fmt(guessed),
          })}
        </Notice>
      ) : null}
      {!run.running && run.failed.length > 0 ? (
        <div className="space-y-3">
          <ErrorNotice>
            {tPlural(strings.failedCount, locale, run.failed.length, {
              count: fmt(run.failed.length),
            })}
          </ErrorNotice>
          <button className={BUTTON_SECONDARY} onClick={onRetry} type="button">
            {strings.retryFailed}
          </button>
        </div>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.wordsHeading}</h3>
        <WordTable run={run} stressMarks={stressMarks} strings={strings} />
      </div>
    </section>
  );
}

export default function EnglishToIpaConverterWidget() {
  const strings = useToolUi(ENGLISH_TO_IPA_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [stressMarks, setStressMarks] = useState(true);
  const [slashes, setSlashes] = useState(false);
  const { run, convert, retry, reset } = usePronunciations();

  return (
    <>
      <TextAreaField
        hint={t(strings.textHint, { max: fmt(MAX_WORDS) })}
        id="ipa-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={6}
        value={text}
      />
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={stressMarks}
          label={strings.stressLabel}
          onChange={setStressMarks}
        />
        <Checkbox
          checked={slashes}
          label={strings.slashesLabel}
          onChange={setSlashes}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={text.trim().length === 0}
          onClick={() => convert(text)}
          type="button"
        >
          {strings.convert}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            convert(strings.sample);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={!(text || run)}
          onClick={() => {
            setText("");
            reset();
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {run ? (
        <Results
          onRetry={retry}
          run={run}
          slashes={slashes}
          stressMarks={stressMarks}
          strings={strings}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
